import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, Link } from 'react-router-dom'
import { signIn, clearError } from '../store/slices/authSlice'
import AuthBrand from '../components/(auth)/AuthBrand'

const SignIn = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  })
  const [showSuccess, setShowSuccess] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [validationError, setValidationError] = useState('')

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { isLoading, isAuthenticated, error } = useSelector(state => state.auth)

  // Clear error when component mounts
  useEffect(() => {
    // Don't clear error on mount to preserve error messages
    // dispatch(clearError())
  }, [dispatch])

  // Navigate to dashboard if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard')
    }
  }, [isAuthenticated, navigate])

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
    // Clear validation error when user starts typing
    if (validationError) {
      setValidationError('')
    }
    // Clear Redux error when user starts typing
    if (error) {
      dispatch(clearError())
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    e.stopPropagation()
    setShowSuccess(false)
    setValidationError('')
    dispatch(clearError())

    if (!formData.email || !formData.password) {
      setValidationError('Please fill in all fields')
      return
    }

    try {
      const result = await dispatch(signIn(formData))
      
      if (result.type === 'auth/signIn/fulfilled') {
        setShowSuccess(true)
        // Show success message for 2.5 seconds then navigate
        setTimeout(() => {
          navigate('/dashboard')
        }, 2500)
      }
    } catch (err) {
      console.error('Unexpected error:', err)
    }
  }

  return (
    <div style={styles.container}>
      <style>
        {`
          input::placeholder {
            color: #BFBFBF !important;
          }
        `}
      </style>
      <form style={styles.form} onSubmit={handleSubmit}>
        <AuthBrand />
        <h1 style={styles.title}>Sign in</h1>
        
        {(error || validationError) && (
          <div style={styles.globalError}>
            <span>{error || validationError}</span>
            <button 
              type="button"
              onClick={() => {
                setValidationError('')
                dispatch(clearError())
              }}
              style={styles.clearErrorButton}
            >
              ×
            </button>
          </div>
        )}

        {showSuccess && (
          <div style={styles.successMessage}>
            Login successful! Redirecting...
          </div>
        )}

        <div style={styles.fieldGroup}>
          <label style={styles.label}>Email Address</label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="email address"
            style={styles.input}
            onFocus={(e) => {
              e.target.style.borderColor = '#2563eb'
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#BFBFBF'
            }}
          />
        </div>

        <div style={styles.fieldGroup}>
          <label style={styles.label}>Password</label>
          <div style={styles.inputContainer}>
            <input 
              type={showPassword ? "text" : "password"}
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="password"
              style={styles.input}
              onFocus={(e) => {
                e.target.style.borderColor = '#2563eb'
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#BFBFBF'
              }}
            />
            <span 
              style={styles.eyeIcon}
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? '👁️' : '👁️‍🗨️'}
            </span>
          </div>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          style={styles.button}
          onMouseEnter={(e) => {
            if (!isLoading) {
              e.target.style.backgroundColor = '#1d4ed8'
            }
          }}
          onMouseLeave={(e) => {
            if (!isLoading) {
              e.target.style.backgroundColor = '#2563eb'
            }
          }}
        >
          {isLoading ? 'Signing in...' : 'Sign in'}
        </button>

        <div style={styles.linkContainer}>
          <p style={styles.linkText}>
            Don't have an account?{' '}
            <a href="/signup" style={styles.link}>
              Sign up
            </a>
          </p>
       
        </div>
      </form>
    </div>
  )
}

export default SignIn

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#ffffff',
    padding: '20px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  form: {
    width: '200%',
    maxWidth: '600px',
    backgroundColor: '#ffffff',
    padding: '40px',
    borderRadius: '8px',
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1e293b',
    textAlign: 'center',
    marginBottom: '32px',
    margin: '0 0 24px 0'
  },
  fieldGroup: {
    marginBottom: '24px',
  },
  inputContainer: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center'
  },
  eyeIcon: {
    position: 'absolute',
    right: '12px',
    cursor: 'pointer',
    fontSize: '16px',
    userSelect: 'none'
  },
  label: {
    display: 'block',
    fontSize: '16px',
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: '9px'
  },
  input: {
    width: '100%',
    padding: '12px 16px',
    fontSize: '16px',
    border: '1px solid #BFBFBF',
    borderRadius: '6px',
    backgroundColor: '#ffffff',
    color: '#1e293b',
    outline: 'none',
    transition: 'border-color 0.2s ease',
    boxSizing: 'border-box'
  },
  button: {
    width: '100%',
    padding: '12px 24px',
    fontSize: '16px',
    fontWeight: '600',
    color: '#ffffff',
    backgroundColor: '#2563eb',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    marginTop: '8px'
  },
  globalError: {
    color: '#ef4444',
    fontSize: '14px',
    textAlign: 'center',
    marginBottom: '16px',
    padding: '8px',
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '4px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  clearErrorButton: {
    background: 'none',
    border: 'none',
    color: '#ef4444',
    fontSize: '18px',
    cursor: 'pointer',
    padding: '0',
    marginLeft: '8px',
    fontWeight: 'bold'
  },
  successMessage: {
    color: '#059669',
    fontSize: '14px',
    textAlign: 'center',
    marginBottom: '16px',
    padding: '8px',
    backgroundColor: '#f0fdf4',
    border: '1px solid #bbf7d0',
    borderRadius: '4px'
  },
  linkContainer: {
    textAlign: 'center',
    marginTop: '16px'
  },
  linkText: {
    fontSize: '14px',
    color: '#6b7280'
  },
  link: {
    color: '#2563eb',
    textDecoration: 'none',
    fontWeight: '500'
  }
}